import { attr, computedOf, html, MapSignal, on, type Prop } from '@tempots/dom'
import { AgencyAssetService } from '@/services/agency-asset-service'
import { normalizeHexColor } from '@/utils/color'
import {
  buildFormDesign,
  formWriteBackIntents,
  type WizardState,
} from '../wizard-state'

function textField(label: string, value: Prop<string>, placeholder = '') {
  return html.div(
    attr.class('wiz-field'),
    html.label(label),
    html.input(
      attr.type('text'),
      attr.value(value),
      attr.placeholder(placeholder),
      on.input((e: Event) => value.set((e.target as HTMLInputElement).value))
    )
  )
}

function colorField(label: string, value: Prop<string>) {
  return html.div(
    attr.class('wiz-field'),
    html.label(label),
    html.input(
      attr.type('color'),
      attr.value(value),
      on.input((e: Event) => value.set((e.target as HTMLInputElement).value))
    ),
    // Editable hex value next to the swatch — the browser's picker popup
    // defaults to RGB and can't be configured, so we show hex ourselves.
    html.input(
      attr.type('text'),
      attr.class('wiz-hex-input'),
      attr.maxlength(7),
      attr.spellcheck(false),
      attr.value(value.map(v => v.toUpperCase())),
      on.keydown((e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
          e.preventDefault()
          ;(e.target as HTMLInputElement).blur()
        }
      }),
      on.change((e: Event) => {
        const input = e.target as HTMLInputElement
        const norm = normalizeHexColor(input.value)
        input.value = (norm ?? value.value).toUpperCase()
        if (norm) value.set(norm)
      })
    )
  )
}

export function InfoStep(state: WizardState, agencyId: string) {
  const uploadLogo = async (file: File) => {
    try {
      const res = await AgencyAssetService.uploadAgencyAsset(
        agencyId,
        'headerLogo',
        file
      )
      state.logoAssetKey.set(res.key)
    } catch (error) {
      console.error('Logo upload failed', error)
    }
  }

  // The one field with no write-back: an edited name is used in the header
  // as written, without renaming the agency's account.
  const nameEdited = computedOf(
    state.agencyName,
    state.profile
  )((name, p) => {
    const t = name.trim()
    return Boolean(t) && t !== p.agencyName
  })

  return html.div(
    attr.class('wiz-form'),
    html.div(attr.class('wiz-section-title'), 'Your agency'),
    html.p(
      attr.class('wiz-section-hint'),
      'Saved to your Agency profile and kept in sync — update them once later, and every header follows.'
    ),
    textField('Agency name', state.agencyName),
    MapSignal(nameEdited, edited =>
      edited
        ? html.div(
            attr.class('wiz-field-note'),
            `Used in your header as written. Your agency’s account name stays “${state.profile.value.agencyName}”.`
          )
        : html.span()
    ),
    textField('Phone', state.agencyPhone, '(555) 123-4567'),
    textField('Website', state.agencyUrl, 'www.example.com'),
    textField('Email', state.agencyEmail, 'info@example.com'),
    textField('LinkedIn', state.agencyLinkedIn),

    html.div(attr.class('wiz-section-title'), 'Logo'),
    html.div(
      attr.class('wiz-field'),
      html.label('Agency logo'),
      html.div(
        attr.style('display:flex; align-items:center; gap:12px; flex:1;'),
        MapSignal(state.logoAssetKey, key =>
          key
            ? html.img(
                attr.src(
                  AgencyAssetService.getDesignImageAssetUrl(agencyId, key)
                ),
                attr.style(
                  'height:44px; max-width:160px; object-fit:contain; border:1px solid #e5e7eb; border-radius:4px; padding:2px; background:white;'
                )
              )
            : html.span(
                attr.class('wiz-hint'),
                'No logo yet — optional but recommended.'
              )
        ),
        html.button(
          attr.type('button'),
          attr.class('wiz-btn wiz-btn-secondary'),
          on.click(() => {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = 'image/*'
            input.onchange = () => {
              const file = input.files?.[0]
              if (file) void uploadLogo(file)
            }
            input.click()
          }),
          MapSignal(state.logoAssetKey, key =>
            html.span(key ? 'Replace logo' : 'Upload logo')
          )
        )
      )
    ),

    html.div(attr.class('wiz-section-title'), 'Colors'),
    colorField('Brand color', state.brandColor),
    colorField('Text color', state.textColor),

    html.div(attr.class('wiz-section-title'), 'You'),
    html.p(
      attr.class('wiz-section-hint'),
      'Saved to your Profile. On teammates’ resumes ',
      html.b('their details appear here instead'),
      ' — everyone automatically gets their own.'
    ),
    textField('Your name', state.recruiterName),
    textField('Your phone', state.recruiterPhone),
    textField('Your email', state.recruiterEmail),
    textField('Your LinkedIn', state.recruiterLinkedIn),

    html.div(attr.class('wiz-section-title'), 'Footer'),
    html.div(
      attr.class('wiz-field'),
      html.label('Footer band'),
      html.div(
        attr.style('display:flex; align-items:center; gap:8px; flex:1;'),
        MapSignal(state.includeFooter, inc =>
          html.button(
            attr.type('button'),
            attr.class(
              inc ? 'wiz-btn wiz-btn-primary' : 'wiz-btn wiz-btn-secondary'
            ),
            on.click(() => state.includeFooter.set(!state.includeFooter.value)),
            inc ? 'Footer on' : 'Footer off'
          )
        ),
        html.span(
          attr.class('wiz-hint'),
          'Repeats the same details at the bottom of each page.'
        )
      )
    ),

    html.div(
      attr.style('margin-top:16px;'),
      html.button(
        attr.type('button'),
        attr.class('wiz-btn wiz-btn-primary'),
        on.click(() => {
          state.writeBacks.set(formWriteBackIntents(state))
          state.result.set(buildFormDesign(state))
          state.step.set('preview')
        }),
        'Preview my design →'
      )
    )
  )
}
